import React, { useRef, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useGetAidsToNavigationFileContentQuery,
  useGetFairwayFileContentQuery,
  useGetGeoJsonFileContentQuery,
  useUpdateGeoJsonFeatureMutation,
} from '@/services/RoutingManagementService';
import mapboxgl from 'mapbox-gl';
import { KeenIcon } from '@/components';
import { useGetIconImagesQuery } from '@/services/IconImagesManagementService';
import NumberInput from '@/components/inputControls/NumberInput';

import { useMapboxAccessToken } from '@/hooks/useMapboxAccessToken';
import { useSnackbar } from 'notistack';
interface RouteFeatureProperties {
  nimifi?: string;
  min_height?: number | string;
  vayalue_sy?: number | string;
  [key: string]: unknown;
}

export const RouteDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const fileId = Number(id);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [webglError, setWebglError] = useState(false);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const selectedFeatureRef = useRef<number | string | null>(null);
  const [selectedFeatureProperties, setSelectedFeatureProperties] =
    useState<RouteFeatureProperties | null>(null);
  const [isEditingDimensions, setIsEditingDimensions] = useState(false);
  const [editHeight, setEditHeight] = useState<string>('');
  const [editDepth, setEditDepth] = useState<string>('');
  const {
    data: routeData,
    isLoading: isRouteLoading,
    isFetching: isRouteFetching,
    isError: isRouteError,
    error: routeError,
    refetch,
  } = useGetGeoJsonFileContentQuery(fileId);
  const [updateGeoJsonFeature, { isLoading: isUpdatingFeature }] =
    useUpdateGeoJsonFeatureMutation();
  const {
    data: navigationData,
    isLoading: isNavLoading,
    isError: isNavError,
    error: navError,
  } = useGetAidsToNavigationFileContentQuery(fileId);
  const {
    data: fairwayData,
    isLoading: isFairwayLoading,
    isError: isFairwayError,
    error: fairwayError,
  } = useGetFairwayFileContentQuery(fileId);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const iconImagesResult = useGetIconImagesQuery({
    lang: 'en',
  });
  const iconImages = iconImagesResult?.data;
  const { isMapboxReady } = useMapboxAccessToken();
  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (!mapContainerRef.current || !isMapboxReady) return;

    // Clean up previous map instance
    if (mapRef.current) {
      try {
        if (!mapRef.current._removed) {
          mapRef.current.remove();
        }
      } catch (err) {
        console.warn('Error removing map instance:', err);
      }
      mapRef.current = null;
    }

    // Determine center and bounds from available data
    let center: [number, number] = [24.9384, 60.1699];

    if (
      routeData?.geojson_content?.features &&
      routeData.geojson_content.features.length > 0
    ) {
      const geometry = routeData.geojson_content.features[0].geometry;
      if (geometry.type !== 'GeometryCollection' && 'coordinates' in geometry) {
        const coords = (
          geometry as GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon
        ).coordinates;
        if (Array.isArray(coords) && coords.length > 0) {
          if (Array.isArray(coords[0]) && coords[0].length >= 2) {
            const firstCoord = coords[0] as [number, number];
            center = [firstCoord[0], firstCoord[1]];
          } else if (coords.length >= 2 && typeof coords[0] === 'number') {
            center = [coords[0] as number, coords[1] as number];
          }
        }
      }
    } else if (navigationData?.[0]?.geojson?.features?.length > 0) {
      const firstFeature = navigationData[0].geojson.features[0];
      if (firstFeature.geometry.type === 'Point') {
        center = firstFeature.geometry.coordinates as [number, number];
      }
    }

    if (!mapboxgl.supported()) {
      setWebglError(true);
      return;
    }

    try {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/hmesikam/cmhkf5ut7001v01sgdjrdhn2b',
        center,
        zoom: 8,
      });
    } catch (e) {
      console.error('WebGL Error:', e);
      setWebglError(true);
      return;
    }

    const loadImages = async (map: mapboxgl.Map) => {
      if (!iconImages?.data) return;

      const promises = iconImages.data.map(
        (icon) =>
          new Promise<void>((resolve) => {
            const isSvg = icon.imageUrl.toLowerCase().endsWith('.svg');

            if (isSvg) {
              // Handle SVG by rendering to canvas
              const img = new Image();
              img.crossOrigin = 'anonymous'; // Handle CORS if needed
              img.src = icon.imageUrl;

              img.onload = () => {
                // Create a canvas to render the SVG
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                  resolve();
                  return;
                }

                // Set canvas to the original SVG size
                canvas.width = img.naturalWidth || 64; // Fallback to 64 if size is unavailable
                canvas.height = img.naturalHeight || 64;

                // Draw the SVG image onto the canvas
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                // Convert canvas to PNG data URL
                const dataUrl = canvas.toDataURL('image/png');

                // Load the rasterized image into Mapbox
                try {
                  if (mapRef.current === map && !map._removed) {
                    map.loadImage(dataUrl, (error, image) => {
                      if (error || !image) {
                        resolve();
                      } else {
                        try {
                          if (mapRef.current === map && !map._removed) {
                            map.addImage(icon.name, image);
                          }
                        } catch (e) {
                          console.warn('Map addImage failed:', e);
                        }
                        resolve();
                      }
                    });
                  } else {
                    resolve();
                  }
                } catch (e) {
                  console.warn('Map loadImage failed:', e);
                  resolve();
                }
              };

              img.onerror = () => {
                resolve();
              };
            } else {
              // Handle PNG or other raster images
              try {
                if (mapRef.current === map && !map._removed) {
                  map.loadImage(icon.imageUrl, (error, image) => {
                    if (error || !image) {
                      resolve();
                    } else {
                      try {
                        if (mapRef.current === map && !map._removed) {
                          map.addImage(icon.name, image);
                        }
                      } catch (e) {
                        console.warn('Map addImage failed:', e);
                      }
                      resolve();
                    }
                  });
                } else {
                  resolve();
                }
              } catch (e) {
                console.warn('Map loadImage failed:', e);
                resolve();
              }
            }
          })
      );

      await Promise.all(promises);
    };

    mapRef.current.on('load', async () => {
      if (!mapRef.current) return;

      const currentMap = mapRef.current;

      try {
        // Load all icons
        await loadImages(currentMap);

        if (mapRef.current !== currentMap || currentMap._removed) return;

        // Add route source and layer if available
        if (routeData) {
          if (currentMap.getSource('route')) {
            currentMap.removeLayer('route-line');
            currentMap.removeSource('route');
          }

          const featuresWithId = (routeData.geojson_content?.features ?? []).map(
            (feature, index) => ({
              ...feature,
              id: feature.id ?? index,
            })
          );

          currentMap.addSource('route', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: featuresWithId,
            },
          });

          currentMap.addLayer({
            id: 'route-line',
            type: 'line',
            source: 'route',
            paint: {
              'line-color': [
                'case',
                ['boolean', ['feature-state', 'selected'], false],
                '#ff0000',
                '#007cbf',
              ],
              'line-width': [
                'case',
                ['boolean', ['feature-state', 'selected'], false],
                6,
                4,
              ],
            },
          });

          // Click handler for route features
          currentMap.on('click', 'route-line', (e) => {
            const mapEvent = e as mapboxgl.MapMouseEvent & {
              features?: mapboxgl.MapboxGeoJSONFeature[];
              _handledByLayer?: boolean;
            };
            if (
              !mapEvent.features ||
              mapEvent.features.length === 0 ||
              mapRef.current !== currentMap ||
              currentMap._removed
            )
              return;

            // Prevent the map click handler from clearing the selection
            mapEvent._handledByLayer = true;

            const feature = mapEvent.features[0];
            const featureId = feature.id;

            if (featureId !== undefined) {
              try {
                // Clear previous highlight
                if (selectedFeatureRef.current !== null) {
                  currentMap.setFeatureState(
                    { source: 'route', id: selectedFeatureRef.current },
                    { selected: false }
                  );
                }

                // Set new highlight
                selectedFeatureRef.current = featureId;
                currentMap.setFeatureState(
                  { source: 'route', id: featureId },
                  { selected: true }
                );
              } catch (e) {
                console.warn('Set feature state failed:', e);
              }

              // Update properties for the bottom panel
              const props = feature.properties as RouteFeatureProperties;
              setSelectedFeatureProperties(props);
              setIsEditingDimensions(false);
              setEditHeight(String(props.min_height ?? ''));
              setEditDepth(String(props.vayalue_sy ?? ''));
            }
          });

          // Click handler for map background to clear selection
          currentMap.on('click', (e) => {
            const mapEvent = e as mapboxgl.MapMouseEvent & {
              _handledByLayer?: boolean;
            };
            if (
              !mapEvent._handledByLayer &&
              selectedFeatureRef.current !== null &&
              mapRef.current === currentMap &&
              !currentMap._removed
            ) {
              try {
                currentMap.setFeatureState(
                  { source: 'route', id: selectedFeatureRef.current },
                  { selected: false }
                );
              } catch (e) {
                console.warn('Clear feature state failed:', e);
              }
              selectedFeatureRef.current = null;
              setSelectedFeatureProperties(null);
              setIsEditingDimensions(false);
            }
          });

          // Change cursor on hover
          currentMap.on('mouseenter', 'route-line', () => {
            if (mapRef.current === currentMap && !currentMap._removed) {
              try {
                currentMap.getCanvas().style.cursor = 'pointer';
              } catch (e) {
                console.warn('Get canvas failed:', e);
              }
            }
          });

          currentMap.on('mouseleave', 'route-line', () => {
            if (mapRef.current === currentMap && !currentMap._removed) {
              try {
                currentMap.getCanvas().style.cursor = '';
              } catch (e) {
                console.warn('Get canvas failed:', e);
              }
            }
          });
        }

        if (mapRef.current !== currentMap || currentMap._removed) return;

        // Add navigation aids source and layer (always clean up existing first)
        const aidsGeojson = navigationData?.[0]?.geojson ?? {
          type: 'FeatureCollection',
          features: [],
        };

        if (currentMap.getSource('aids')) {
          currentMap.removeLayer('aids-points');
          currentMap.removeSource('aids');
        }

        currentMap.addSource('aids', {
          type: 'geojson',
          data: aidsGeojson,
        });

        currentMap.addLayer({
          id: 'aids-points',
          type: 'symbol',
          source: 'aids',
          layout: {
            'icon-image': [
              'case',
              [
                'all',
                ['==', ['get', 'marker_type'], 'Remmare'],
                ['==', ['get', 'position'], 'Ost'],
              ],
              'Cardinal East 2',
              [
                'all',
                ['==', ['get', 'marker_type'], 'Remmare'],
                ['==', ['get', 'position'], 'Väst'],
              ],
              'Cardinal West 2',
              [
                'all',
                ['==', ['get', 'marker_type'], 'Remmare'],
                ['==', ['get', 'position'], 'Nord'],
              ],
              'Cardinal North 2',
              [
                'all',
                ['==', ['get', 'marker_type'], 'Remmare'],
                ['==', ['get', 'position'], 'Syd'],
              ],
              'Cardinal South 2',
              [
                'all',
                ['==', ['get', 'marker_type'], 'Remmare'],
                ['==', ['get', 'position'], 'Grön'],
              ],
              'Lateral Green 1',
              [
                'all',
                ['==', ['get', 'marker_type'], 'Remmare'],
                ['==', ['get', 'position'], 'Röd'],
              ],
              'Lateral Red 1',
              [
                'all',
                ['==', ['get', 'marker_type'], 'Remmare'],
                ['==', ['get', 'position'], 'Kryss'],
              ],
              'Isolated Danger 2',
              [
                'all',
                ['==', ['get', 'marker_type'], 'Tavla'],
                ['==', ['get', 'position'], 'Nedre'],
              ],
              'square',
              [
                'all',
                ['==', ['get', 'marker_type'], 'Tavla'],
                ['==', ['get', 'position'], 'Övre'],
              ],
              'square',
              ['==', ['get', 'marker_type'], 'Boj'],
              'Lateral Yellow 2',
              'marker',
            ],
            'icon-size': 1,
            'icon-allow-overlap': true,
          },
        });

        // Add fairways source and layer if available
        if (fairwayData?.[0]?.geojson) {
          if (currentMap.getSource('fairways')) {
            currentMap.removeLayer('fairways-fill');
            currentMap.removeSource('fairways');
          }
          currentMap.addSource('fairways', {
            type: 'geojson',
            data: fairwayData[0].geojson,
          });
          currentMap.addLayer({
            id: 'fairways-fill',
            type: 'fill',
            source: 'fairways',
            paint: {
              'fill-color': '#006A67',
              'fill-opacity': 0.3,
              'fill-outline-color': '#006A67',
            },
          });
        }

        // Once the map is fully loaded and idle (all layers rendered)
        currentMap.once('idle', () => {
          if (mapRef.current === currentMap && !currentMap._removed) {
            setIsMapLoading(false);
          }
        });
      } catch (err) {
        console.error('Error during Mapbox layer setup:', err);
      }
    });

    return () => {
      if (mapRef.current) {
        try {
          if (!mapRef.current._removed) {
            mapRef.current.remove();
          }
        } catch (err) {
          console.warn('Error removing map instance:', err);
        }
        mapRef.current = null;
      }
    };
  }, [routeData, navigationData, fairwayData, iconImages, isMapboxReady]);

  const isLoading = isRouteLoading || isNavLoading || isFairwayLoading;
  const isError = isRouteError || isNavError || isFairwayError;
  const error = routeError || navError || fairwayError;

  const handleSaveDimensions = async () => {
    if (selectedFeatureRef.current === null || !selectedFeatureProperties)
      return;

    const formData = new FormData();
    formData.append('height', editHeight);
    formData.append('depth', editDepth);

    try {
      await updateGeoJsonFeature({
        fileId,
        featureIndex: Number(selectedFeatureRef.current),
        payload: formData,
      }).unwrap();

      enqueueSnackbar('Segment dimensions updated successfully', {
        variant: 'success',
      });
      setIsEditingDimensions(false);

      if (mapRef.current && selectedFeatureRef.current !== null) {
        mapRef.current.setFeatureState(
          { source: 'route', id: selectedFeatureRef.current },
          { selected: false }
        );
        selectedFeatureRef.current = null;
      }
      setSelectedFeatureProperties(null);

      refetch();
    } catch (e) {
      console.error('Failed to update segment dimensions', e);
      enqueueSnackbar('Failed to update segment dimensions', {
        variant: 'error',
      });
    }
  };

  return (
    <div className="container mx-auto p-4 sm:p-6 lg:p-8">
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-2 text-gray-600">
            <KeenIcon icon="spinner" className="animate-spin text-2xl" />
            <span className="text-lg">Loading route...</span>
          </div>
        </div>
      ) : isError ? (
        <div className="card border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 font-medium">
            Error loading route: {error?.toString()}
          </p>
        </div>
      ) : (!routeData?.geojson_content?.features ||
          routeData.geojson_content.features.length === 0) &&
        (!navigationData?.[0]?.geojson?.features ||
          navigationData[0].geojson.features.length === 0) &&
        (!fairwayData?.[0]?.geojson?.features ||
          fairwayData[0].geojson.features.length === 0) ? (
        <div className="card bg-gray-100 border border-gray-200 rounded-lg p-6 text-center">
          <p className="text-gray-600 font-medium">
            No route or navigation data found.
          </p>
        </div>
      ) : (
        <div className="card bg-gray-100 rounded-lg overflow-hidden">
          <div className="card-header flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Go back to route listings"
                title="Back to Routes"
              >
                <KeenIcon icon="arrow-left" className="text-gray-600 text-lg" />
              </button>
              <h2 className="text-xl font-semibold text-gray-800">
                Route Details
              </h2>
            </div>
            <div className="flex items-center gap-2 text-gray-600 contrast:text-white">
              <KeenIcon icon="map" className="text-lg" />
              <span className="text-sm">GeoJSON Route Visualization</span>
            </div>
          </div>
          <div className="card-body p-6">
            {webglError ? (
              <div className="w-full min-h-[500px] flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200 p-4 sm:p-8 shadow-sm">
                <div className="max-w-md text-center">
                  <div className="flex justify-center mb-4">
                    <KeenIcon
                      icon="shield-cross"
                      className="text-4xl text-danger"
                    />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Map Display Not Available
                  </h3>
                  <p className="text-gray-600 mb-6 font-medium">
                    The map cannot be displayed because WebGL (hardware
                    acceleration) is not available in your browser.
                  </p>
                  <div className="bg-gray-200 p-5 rounded-xl border border-gray-200 text-left shadow-sm">
                    <p className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-3">
                      How to enable
                    </p>
                    <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                      {`To enable hardware acceleration:

• Chrome/Edge: Go to Settings → System → Enable "Use hardware acceleration when available"
• Firefox: Go to Settings → General → Performance → Uncheck "Use recommended performance settings" → Check "Use hardware acceleration when available"

After making changes, please restart your browser.`}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative w-full h-[500px] sm:h-[600px] lg:h-[700px]">
                {(isMapLoading || isRouteFetching) && !isError && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-gray-50/80 backdrop-blur-sm rounded-lg border border-gray-200">
                    <KeenIcon
                      icon="setting-2"
                      className="animate-spin text-4xl text-primary mb-4"
                    />
                    <p className="text-gray-700 font-medium animate-pulse">
                      Loading map content...
                    </p>
                  </div>
                )}
                <div
                  ref={mapContainerRef}
                  className="w-full h-full rounded-lg"
                  aria-label="Map displaying route"
                />
              </div>
            )}
            {selectedFeatureProperties && (
              <div className="mt-4 py-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div className="flex items-center justify-between mb-4 border-b pb-2">
                  <h3 className="text-xl mt-10 font-bold text-gray-800">
                    {selectedFeatureProperties.nimifi ||
                      'Route Segment Details'}
                  </h3>
                  <button
                    onClick={() => {
                      if (
                        mapRef.current &&
                        selectedFeatureRef.current !== null
                      ) {
                        mapRef.current.setFeatureState(
                          { source: 'route', id: selectedFeatureRef.current },
                          { selected: false }
                        );
                        selectedFeatureRef.current = null;
                      }
                      setSelectedFeatureProperties(null);
                      setIsEditingDimensions(false);
                    }}
                    className="btn btn-sm btn-icon btn-light h-8 w-8"
                    aria-label="Close details"
                  >
                    <KeenIcon icon="cross" className="text-gray-500" />
                  </button>
                </div>

                <div className="mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Dimensions
                    </h2>
                    {!isEditingDimensions ? (
                      <button
                        className="btn btn-sm btn-light"
                        type="button"
                        onClick={() => setIsEditingDimensions(true)}
                      >
                        Edit Dimensions
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          className="btn btn-sm btn-light"
                          type="button"
                          onClick={() => {
                            setIsEditingDimensions(false);
                            setEditHeight(
                              String(selectedFeatureProperties.min_height ?? '')
                            );
                            setEditDepth(
                              String(selectedFeatureProperties.vayalue_sy ?? '')
                            );
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-sm btn-primary"
                          type="button"
                          onClick={handleSaveDimensions}
                          disabled={isUpdatingFeature}
                        >
                          {isUpdatingFeature ? 'Saving...' : 'Save Dimensions'}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
                    {!isEditingDimensions ? (
                      <>
                        <div className="flex items-center p-3 bg-gray-200 rounded-lg border border-gray-100 shadow-sm">
                          <div className="bg-blue-100 dark:bg-blue-900 contrast:bg-blue-900 p-2 rounded-md mr-3 text-blue-600 dark:text-blue-100 contrast:text-blue-100">
                            <KeenIcon icon="arrow-up" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                              Height
                            </p>
                            <p className="text-lg font-bold text-gray-900">
                              {selectedFeatureProperties.min_height
                                ? `${selectedFeatureProperties.min_height}m`
                                : 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center p-3 bg-gray-200 rounded-lg border border-gray-100 shadow-sm">
                          <div className="bg-emerald-100 dark:bg-emerald-900 contrast:bg-emerald-900 p-2 rounded-md mr-3 text-emerald-600 dark:text-emerald-100 contrast:text-emerald-100">
                            <KeenIcon icon="arrow-down" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                              Depth
                            </p>
                            <p className="text-lg font-bold text-gray-900">
                              {selectedFeatureProperties.vayalue_sy
                                ? `${selectedFeatureProperties.vayalue_sy}m`
                                : 'N/A'}
                            </p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <label
                            htmlFor="segment-height"
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            Height (m)
                          </label>
                          <NumberInput
                            id="segment-height"
                            data-testid="segment-height"
                            className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                            value={editHeight}
                            onChange={(e) => setEditHeight(e.target.value)}
                            autoComplete="off"
                            step="0.1"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="segment-depth"
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            Depth (m)
                          </label>
                          <NumberInput
                            id="segment-depth"
                            data-testid="segment-depth"
                            className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                            value={editDepth}
                            onChange={(e) => setEditDepth(e.target.value)}
                            autoComplete="off"
                            step="0.1"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="p-6"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteDetails;
